import type { JSX } from 'react';

import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import classNames from 'classnames';
import { Check } from 'lucide-react';

import styles from './DropdownMenu.module.css';

// Root component
export function DropdownMenu(
  props: React.ComponentProps<typeof DropdownMenuPrimitive.Root>,
): JSX.Element {
  return <DropdownMenuPrimitive.Root {...props} />;
}

// Trigger component
export function DropdownMenuTrigger(
  props: React.ComponentProps<typeof DropdownMenuPrimitive.Trigger>,
): JSX.Element {
  return <DropdownMenuPrimitive.Trigger {...props} />;
}

// Content component
interface DropdownMenuContentProps
  extends React.ComponentProps<typeof DropdownMenuPrimitive.Content> {
  sideOffset?: number;
  className?: string;
}

export function DropdownMenuContent({
  className,
  sideOffset = 4,
  ...props
}: DropdownMenuContentProps): JSX.Element {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        sideOffset={sideOffset}
        className={classNames(styles.DropdownMenuContent, className)}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
}

// Menu Item component
interface DropdownMenuItemProps extends React.ComponentProps<typeof DropdownMenuPrimitive.Item> {
  variant?: 'default' | 'destructive';
  disabled?: boolean;
  className?: string;
}

export function DropdownMenuItem({
  className,
  variant = 'default',
  disabled,
  ...props
}: DropdownMenuItemProps): JSX.Element {
  return (
    <DropdownMenuPrimitive.Item
      className={classNames(
        styles.DropdownMenuItem,
        {
          [styles.destructive]: variant === 'destructive',
          [styles.disabled]: disabled,
        },
        className,
      )}
      disabled={disabled}
      {...props}
    />
  );
}

// Separator component
interface DropdownMenuSeparatorProps
  extends React.ComponentProps<typeof DropdownMenuPrimitive.Separator> {
  className?: string;
}

export function DropdownMenuSeparator({
  className,
  ...props
}: DropdownMenuSeparatorProps): JSX.Element {
  return (
    <DropdownMenuPrimitive.Separator
      className={classNames(styles.DropdownMenuSeparator, className)}
      {...props}
    />
  );
}

// Label component
interface DropdownMenuLabelProps extends React.ComponentProps<typeof DropdownMenuPrimitive.Label> {
  className?: string;
}

export function DropdownMenuLabel({ className, ...props }: DropdownMenuLabelProps): JSX.Element {
  return (
    <DropdownMenuPrimitive.Label
      className={classNames(styles.DropdownMenuLabel, className)}
      {...props}
    />
  );
}

// Group component
export function DropdownMenuGroup(
  props: React.ComponentProps<typeof DropdownMenuPrimitive.Group>,
): JSX.Element {
  return <DropdownMenuPrimitive.Group {...props} />;
}

// Checkbox Item component
interface DropdownMenuCheckboxItemProps
  extends React.ComponentProps<typeof DropdownMenuPrimitive.CheckboxItem> {
  className?: string;
  children: React.ReactNode;
}

export function DropdownMenuCheckboxItem({
  className,
  children,
  ...props
}: DropdownMenuCheckboxItemProps): JSX.Element {
  return (
    <DropdownMenuPrimitive.CheckboxItem
      className={classNames(styles.DropdownMenuCheckboxItem, className)}
      {...props}
    >
      <span className={styles.IndicatorWrapper} aria-hidden="true">
        <DropdownMenuPrimitive.ItemIndicator>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  );
}

// Radio Group component
export function DropdownMenuRadioGroup(
  props: React.ComponentProps<typeof DropdownMenuPrimitive.RadioGroup>,
): JSX.Element {
  return <DropdownMenuPrimitive.RadioGroup {...props} />;
}

// Radio Item component
interface DropdownMenuRadioItemProps
  extends React.ComponentProps<typeof DropdownMenuPrimitive.RadioItem> {
  className?: string;
  children: React.ReactNode;
  /**
   * How the chosen item is marked. `dot` (default) is the classic radio mark;
   * `check` marks it with a tick, for menus where a filled dot reads as a
   * status light rather than "this one is selected".
   */
  indicator?: 'dot' | 'check';
}

export function DropdownMenuRadioItem({
  className,
  children,
  indicator = 'dot',
  ...props
}: DropdownMenuRadioItemProps): JSX.Element {
  return (
    <DropdownMenuPrimitive.RadioItem
      className={classNames(styles.DropdownMenuRadioItem, className)}
      {...props}
    >
      <span className={styles.IndicatorWrapper} aria-hidden="true">
        <DropdownMenuPrimitive.ItemIndicator>
          {indicator === 'check' ? (
            <Check aria-hidden="true" />
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              width="8"
              height="8"
            >
              <circle cx="12" cy="12" r="12" />
            </svg>
          )}
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.RadioItem>
  );
}

// Sub Menu component
export function DropdownMenuSub(
  props: React.ComponentProps<typeof DropdownMenuPrimitive.Sub>,
): JSX.Element {
  return <DropdownMenuPrimitive.Sub {...props} />;
}

// Sub Trigger component
export function DropdownMenuSubTrigger(
  props: React.ComponentProps<typeof DropdownMenuPrimitive.SubTrigger>,
): JSX.Element {
  return <DropdownMenuPrimitive.SubTrigger {...props} />;
}

// Sub Content component
export function DropdownMenuSubContent(
  props: React.ComponentProps<typeof DropdownMenuPrimitive.SubContent>,
): JSX.Element {
  return <DropdownMenuPrimitive.SubContent {...props} />;
}
